import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Grid, CircularProgress, Alert, Chip,
  TextField, InputAdornment, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  HourglassTop as PendingIcon,
  Warning as OverdueIcon,
  CheckCircle as NormalIcon,
  Inventory2 as CartonIcon,
  Scale as WeightIcon,
  Visibility as ViewIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  ViewModule as CardsIcon,
  ViewList as TableIcon,
} from '@mui/icons-material';

import { fetchPendingApprovalsV2, todayStr } from '../utils/reportsApi';
import ExportButtons from '../components/ExportButtons';

// ============================================================================
//  KPI CARD
// ============================================================================
const KPI_COLORS = {
  violet:'#5b21b6', red:'#dc2626', amber:'#ea580c', blue:'#1565c0', green:'#16a34a',
};

function KpiCard({ color, label, value, subtitle, icon: Icon, valueColor }) {
  const bc = KPI_COLORS[color] || KPI_COLORS.violet;
  return (
    <Paper variant="outlined" sx={{
      p: 1.5, borderLeft: `4px solid ${bc}`, transition: 'all 0.15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(0,0,0,0.06)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: 'text.secondary', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        {Icon && <Icon sx={{ color: bc, fontSize: 18, opacity: 0.7 }} />}
      </Box>
      <Typography sx={{
        fontSize: 26, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
        lineHeight: 1.1, mt: 0.25, color: valueColor || 'text.primary',
      }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>{subtitle}</Typography>
    </Paper>
  );
}

// ============================================================================
//  Age badge
// ============================================================================
function AgeBadge({ hours }) {
  const overdue = hours > 24;
  const days = Math.floor(hours / 24);
  const hrs  = Math.round(hours - days * 24);
  const label = days > 0 ? `${days}d ${hrs}h` : `${hrs}h`;
  return (
    <Chip label={label} size="small" sx={{
      bgcolor: overdue ? '#fee2e2' : '#fef3c7',
      color:   overdue ? '#991b1b' : '#92400e',
      fontWeight: 700, fontSize: 10.5, height: 20,
      '& .MuiChip-label': { px: 1 },
    }} />
  );
}

// ============================================================================
//  Approval Card (used in cards view)
// ============================================================================
function ApprovalCard({ doc, onView, onApprove, onReject }) {
  return (
    <Paper variant="outlined" sx={{
      p: 1.5, borderLeft: doc.age_hours > 24 ? '4px solid #dc2626' : '4px solid #ea580c',
      transition: 'all 0.15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(0,0,0,0.06)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
        <Box component="span" sx={{
          fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#5b21b6',
          bgcolor: '#ede9fe', px: 0.875, py: 0.25, borderRadius: 0.5, fontSize: 12,
        }}>
          {doc.gateout_no}
        </Box>
        <AgeBadge hours={doc.age_hours} />
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>
        {doc.buyer || '—'} <Box component="span" sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 500 }}>· {doc.destination_port || '—'}</Box>
      </Typography>
      <Typography sx={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'text.disabled', mt: 0.25 }}>
        {doc.container_no || 'No container'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, fontSize: 11.5 }}>
        <Box><Box component="span" sx={{ fontWeight: 700 }}>{doc.total_pallets}</Box> pallets</Box>
        <Box>·</Box>
        <Box><Box component="span" sx={{ fontWeight: 700 }}>{doc.total_cartons.toLocaleString()}</Box> cartons</Box>
        <Box>·</Box>
        <Box><Box component="span" sx={{ fontWeight: 700 }}>{(doc.total_kgs/1000).toFixed(1)}k</Box> KG</Box>
      </Box>
      <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5 }}>
        Submitted by <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>{doc.submitted_by || 'Unknown'}</Box>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
        <Button size="small" variant="outlined" startIcon={<ViewIcon sx={{ fontSize: 14 }} />}
                onClick={() => onView?.(doc)} sx={{ fontSize: 11, textTransform: 'none', py: 0.25 }}>
          View
        </Button>
        <Button size="small" variant="contained" startIcon={<ApproveIcon sx={{ fontSize: 14 }} />}
                onClick={() => onApprove?.(doc)}
                sx={{
                  fontSize: 11, textTransform: 'none', py: 0.25,
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  '&:hover': { background: 'linear-gradient(135deg, #15803d, #14532d)' },
                }}>
          Approve
        </Button>
        <Button size="small" variant="contained" startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
                onClick={() => onReject?.(doc)}
                sx={{
                  fontSize: 11, textTransform: 'none', py: 0.25,
                  background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                  '&:hover': { background: 'linear-gradient(135deg, #991b1b, #7f1d1d)' },
                }}>
          Reject
        </Button>
      </Box>
    </Paper>
  );
}

// ============================================================================
//  MAIN COMPONENT
// ============================================================================
export default function PendingApprovalsPage({ user, showToast, onNavigateTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');     // all | overdue | normal
  const [view, setView] = useState('cards');       // cards | table
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetchPendingApprovalsV2();
      if (res.success) setData(res.data);
      else setError(res.error || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const t = setInterval(loadData, 60000);
    return () => clearInterval(t);
  }, [loadData]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.pending;
    if (filter === 'overdue') rows = rows.filter(r => r.age_hours > 24);
    else if (filter === 'normal') rows = rows.filter(r => r.age_hours <= 24);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.gateout_no || '').toLowerCase().includes(q) ||
        (r.buyer || '').toLowerCase().includes(q) ||
        (r.container_no || '').toLowerCase().includes(q) ||
        (r.destination_port || '').toLowerCase().includes(q) ||
        (r.submitted_by || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, filter, search]);

  const handleView    = (doc) => onNavigateTab?.('fcl_manifest', { fclNo: doc.gateout_no });
  const handleApprove = (doc) => showToast?.('Approve action not wired here — use Cold Store OUT → Approve workflow', 'info');
  const handleReject  = (doc) => showToast?.('Reject action not wired here — use Cold Store OUT → Reject workflow', 'info');

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }
  if (error && !data) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }
  if (!data) return null;

  const s = data.summary;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>⏳ Pending Shipment Approvals</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            All shipment documents awaiting approver action · auto-refreshes every 60s
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" size="small"
                onClick={loadData} disabled={loading}
                sx={{ ml: 'auto', textTransform: 'none' }}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {/* KPI ROW */}
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard color="violet" icon={PendingIcon} label="Total Pending" value={s.total}
                   subtitle="awaiting approver action" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard color="red" icon={OverdueIcon} label="⚠ Overdue (>24h)" value={s.overdue}
                   valueColor="#dc2626" subtitle="needs urgent attention" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard color="amber" icon={NormalIcon} label="Normal (<24h)" value={s.normal}
                   subtitle="within SLA" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard color="blue" icon={CartonIcon} label="Total Cartons" value={s.total_cartons}
                   subtitle="across all pending FCLs" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard color="green" icon={WeightIcon} label="Total Weight"
                   value={(s.total_kgs/1000).toFixed(1) + 'k'}
                   subtitle="KG pending shipment" />
        </Grid>
      </Grid>

      {/* FILTER BAR */}
      <Paper variant="outlined" sx={{
        p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: 'text.secondary', textTransform: 'uppercase' }}>
          Filter:
        </Typography>
        {[
          { id: 'all',     label: `All ${s.total}` },
          { id: 'overdue', label: `⚠ Overdue ${s.overdue}` },
          { id: 'normal',  label: `✓ Normal ${s.normal}` },
        ].map(f => (
          <Button key={f.id} size="small"
                  variant={filter === f.id ? 'contained' : 'outlined'}
                  onClick={() => setFilter(f.id)}
                  sx={{
                    fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25, minWidth: 'auto',
                    ...(filter === f.id ? {
                      background: 'linear-gradient(135deg, #d32f2f, #1565c0)',
                      '&:hover': { background: 'linear-gradient(135deg, #b91c1c, #0d47a1)' },
                    } : {}),
                  }}>
            {f.label}
          </Button>
        ))}

        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: 'text.secondary', textTransform: 'uppercase', ml: 1.5 }}>
          View:
        </Typography>
        <Button size="small" startIcon={<CardsIcon sx={{ fontSize: 14 }} />}
                variant={view === 'cards' ? 'contained' : 'outlined'}
                onClick={() => setView('cards')}
                sx={{
                  fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25,
                  ...(view === 'cards' ? {
                    background: 'linear-gradient(135deg, #d32f2f, #1565c0)',
                    '&:hover': { background: 'linear-gradient(135deg, #b91c1c, #0d47a1)' },
                  } : {}),
                }}>
          Cards
        </Button>
        <Button size="small" startIcon={<TableIcon sx={{ fontSize: 14 }} />}
                variant={view === 'table' ? 'contained' : 'outlined'}
                onClick={() => setView('table')}
                sx={{
                  fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25,
                  ...(view === 'table' ? {
                    background: 'linear-gradient(135deg, #d32f2f, #1565c0)',
                    '&:hover': { background: 'linear-gradient(135deg, #b91c1c, #0d47a1)' },
                  } : {}),
                }}>
          Table
        </Button>

        <TextField
          size="small" placeholder="Search FCL, buyer, container, port, submitter…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
            sx: { fontSize: 12.5 },
          }}
          sx={{ flex: 1, minWidth: 240, ml: 'auto' }} />

        <ExportButtons
          filename={`pending_approvals_${todayStr()}`}
          title="Pending Shipment Approvals"
          subtitle={`${filtered.length} of ${s.total} shown · filter: ${filter}`}
          sheets={[{
            name: 'Pending Approvals',
            headers: ['FCL', 'Age (hours)', 'Buyer', 'Destination', 'Container',
                      'Pallets', 'Cartons', 'KG', 'Submitted At', 'Submitted By'],
            rows: filtered.map(p => [
              p.gateout_no, p.age_hours.toFixed(1), p.buyer || '', p.destination_port || '',
              p.container_no || '', p.total_pallets, p.total_cartons, p.total_kgs,
              p.submitted_at, p.submitted_by || '',
            ]),
          }]} />
      </Paper>

      {/* RESULTS */}
      {filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 48, mb: 1 }}>🎉</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
            All clear · No pending approvals match your filter
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>
            {s.total > 0
              ? `${s.total} total pending — try clearing the search or changing the filter.`
              : `Nothing is waiting for approval right now.`}
          </Typography>
        </Paper>
      ) : view === 'cards' ? (
        <Grid container spacing={1.5}>
          {filtered.map(doc => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
              <ApprovalCard doc={doc}
                            onView={handleView} onApprove={handleApprove} onReject={handleReject} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>FCL</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Age</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Buyer</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Container</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Pallets</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Cartons</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>KG</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Submitted By</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(doc => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box component="span" onClick={() => handleView(doc)}
                         sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#5b21b6',
                               bgcolor: '#ede9fe', px: 0.875, py: 0.25, borderRadius: 0.5, cursor: 'pointer', fontSize: 11 }}>
                      {doc.gateout_no}
                    </Box>
                  </TableCell>
                  <TableCell><AgeBadge hours={doc.age_hours} /></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{doc.buyer || '—'}</TableCell>
                  <TableCell>{doc.destination_port || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                    {doc.container_no || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{doc.total_pallets}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{doc.total_cartons.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{(doc.total_kgs/1000).toFixed(1)}k</TableCell>
                  <TableCell sx={{ fontSize: 11.5 }}>{doc.submitted_by || '—'}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      <Button size="small" variant="outlined" onClick={() => handleView(doc)}
                              sx={{ minWidth: 0, px: 0.75, fontSize: 10.5, textTransform: 'none' }}>
                        <ViewIcon sx={{ fontSize: 14 }} />
                      </Button>
                      <Button size="small" variant="contained" onClick={() => handleApprove(doc)}
                              sx={{
                                minWidth: 0, px: 0.75, fontSize: 10.5, textTransform: 'none',
                                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                                '&:hover': { background: 'linear-gradient(135deg, #15803d, #14532d)' },
                              }}>
                        <ApproveIcon sx={{ fontSize: 14 }} />
                      </Button>
                      <Button size="small" variant="contained" onClick={() => handleReject(doc)}
                              sx={{
                                minWidth: 0, px: 0.75, fontSize: 10.5, textTransform: 'none',
                                background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                                '&:hover': { background: 'linear-gradient(135deg, #991b1b, #7f1d1d)' },
                              }}>
                        <RejectIcon sx={{ fontSize: 14 }} />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
